import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CountdownTimer } from "./CountdownTimer";
import { MINT_START_DATE } from "@/config/mint";
import { useState, useEffect } from "react";

interface HeroSectionProps {
  isPastMintDate: boolean;
}

export function HeroSection({ isPastMintDate }: HeroSectionProps) {
  // Mouse position values for parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Smooth spring animation for mouse movement
  const smoothMouseX = useSpring(mouseX, { damping: 50, stiffness: 400 });
  const smoothMouseY = useSpring(mouseY, { damping: 50, stiffness: 400 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      mouseX.set((e.clientX - centerX) / centerX);
      mouseY.set((e.clientY - centerY) / centerY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const backgroundImage = isPastMintDate 
    ? "url('/brand-images/wallpaper-photos.jpeg')"
    : "url('/brand-images/wallpaper-photos-pixelated.jpeg')";

  const scrollToMint = () => {
    const mintSection = document.getElementById("mint-section");
    if (mintSection) {
      mintSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <motion.div 
        className="absolute inset-0" 
        initial={false}
        animate={{
          backgroundImage: `${backgroundImage}`,
          opacity: 0.4,
        }}
        transition={{ duration: 1 }}
        style={{ 
          backgroundSize: '200% 200%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          imageRendering: isPastMintDate ? 'auto' : 'pixelated',
          x: useTransform(smoothMouseX, [-1, 1], ['4%', '-4%']),
          y: useTransform(smoothMouseY, [-1, 1], ['2%', '-2%']),
          scale: 1.2,
          transformOrigin: 'center center',
        }} 
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="relative z-10 text-center max-w-4xl mx-auto px-4 md:px-6 pt-32 pb-16 md:py-0"
      >
        <div className="space-y-8 md:space-y-8">
          <div className="flex flex-col items-center space-y-6">
            <h1 className="text-base sm:text-xl md:text-4xl lg:text-5xl font-bold text-[#9333EA] px-4">
              Are you under the influence?
            </h1>
          </div>
          <h2 className="text-sm sm:text-lg md:text-2xl lg:text-3xl font-bold text-nft-muted">
            Welcome to Cryptfluencers
          </h2>
          <p className="text-[10px] sm:text-xs md:text-base text-nft-muted max-w-[280px] sm:max-w-xl md:max-w-2xl mx-auto leading-loose md:leading-relaxed">
            A satirical NFT collection featuring the most notorious characters in the crypto space. From degens to influencers, we're bringing the memes to life on three different blockchains.
          </p>
        </div>
        
        <div className="mt-16 md:mt-16">
          {!isPastMintDate && (
            <div className="px-4">
              <p className="text-xs md:text-base text-nft-muted mb-4">Minting starts in:</p>
              <CountdownTimer />
            </div>
          )}
          {isPastMintDate && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Button onClick={scrollToMint} className="bg-[#9333EA] hover:bg-[#9333EA]/80 text-white text-xs sm:text-sm md:text-lg px-4 sm:px-6 md:px-8 py-2 md:py-4">
                Mint Your Cryptfluencer
              </Button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </section>
  );
}